import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AlertFill from "@store/Components/Icons/AlertFill";
import { TYPOGRAPHY } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/Framework/Common/Utilities/breakpointValuesMediaQueries";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import getColor from "@insite/mobius/utilities/getColor";
import { resolveColor } from "@insite/mobius/utilities";

const mapStateToProps = (state: ApplicationState) => {
    return {
        session: getSession(state),
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface LoginCalloutStyles {
    wrapper?: InjectableCss;
    leftWrapper?: InjectableCss;
    rightWrapper?: InjectableCss;
    warningIcon?: IconPresentationProps;
    warningText?: TypographyPresentationProps;
    link?: LinkPresentationProps;
}

export const loginCalloutStyles: LoginCalloutStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            padding: 20px;
            border: 2px solid ${getColor("secondary.main")};
            border-radius: 5px;
            margin: 20px 0;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [
                        { values: ["column", null, null, "row", null], properties: ["flex-direction"] },
                        { values: ["flex-start", null, null, "center", null], properties: ["align-items"] },
                    ],
                    mediaOptions.min,
                )}

            &.sticky {
                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [
                            { values: [null, null, "fixed", null, null], properties: ["position"] },
                            { values: [null, null, "50%", null, null], properties: ["left"] },
                            { values: [null, null, "90%", "60%", null], properties: ["width"] },
                            { values: [null, null, "0", null, null], properties: ["margin"] },
                            { values: [null, null, "2", null, null], properties: ["z-index"] },
                            { values: [null, null, "translateX(-50%)", null, null], properties: ["transform"] },
                            {
                                values: [null, null, resolveColor("common.background", theme), null, null],
                                properties: ["background"],
                            },
                        ],
                        mediaOptions.min,
                    )}
            }
        `,
    },
    leftWrapper: {
        css: css`
            display: flex;
            align-items: center;
            padding-right: 15px;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["20px", null, null, "0", null], properties: ["margin-bottom"] }],
                    mediaOptions.min,
                )}
        `,
    },
    rightWrapper: {
        css: css`
            flex-shrink: 0;
        `,
    },
    warningIcon: {
        src: AlertFill,
        css: css`
            margin-right: 10px;
            color: ${getColor("secondary.main")};
        `,
    },
    warningText: {
        size: TYPOGRAPHY.size.base,
        weight: TYPOGRAPHY.weight.medium,
        css: css`
            margin-left: 10px;
            color: ${getColor("secondary.main")};
        `,
    },
    link: {
        typographyProps: {
            css: css`
                color: ${getColor("common.background")} !important;
                font-size: ${TYPOGRAPHY.size.smaller};
                font-weight: ${TYPOGRAPHY.weight.bold};
                line-height: 1;
            `,
        },
        css: css`
            min-height: 40px;
            background: ${getColor("primary.main")};
            padding: 0 40px;
            border-radius: 4px;

            &:hover,
            &:focus {
                background: ${getColor("secondary.main")};
            }
        `,
    },
};

const styles = loginCalloutStyles;

const LoginCallout = ({ session, signInPageUrl }: Props) => {
    if (session.isAuthenticated) {
        return null;
    }
    const loginCalloutContainer = useRef<HTMLDivElement>(null);
    const loginCalloutAnchor = useRef<HTMLDivElement>(null);
    const [stickyClass, setStickyClass] = useState(false);
    const [headerHeight, setHeaderHeight] = useState<number>(0);

    useEffect(() => {
        const headerElement = document.querySelector("main, header > div") as HTMLElement | null;

        const updateStickyClass = () => {
            const container = loginCalloutContainer.current;
            const anchor = loginCalloutAnchor.current;

            if (!container) return;

            const anchorOffset = anchor?.offsetTop ?? 0;
            const scrollY = window.pageYOffset;

            if (scrollY <= headerHeight || scrollY <= anchorOffset - headerHeight) {
                setStickyClass(false);
            } else if (scrollY >= container.offsetTop - headerHeight) {
                setStickyClass(true);
            }
        };

        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                if (entry.target === headerElement) {
                    const newHeight = entry.contentRect.height;
                    if (newHeight !== headerHeight) {
                        setHeaderHeight(newHeight);
                    }
                }
            }
        });

        if (headerElement) {
            resizeObserver.observe(headerElement);
        }

        window.addEventListener("scroll", updateStickyClass, { passive: true });

        return () => {
            window.removeEventListener("scroll", updateStickyClass);
            resizeObserver.disconnect();
        };
    }, [headerHeight]);

    return (
        <>
            <span ref={loginCalloutAnchor}></span>
            <StyledWrapper
                {...styles.wrapper}
                ref={loginCalloutContainer}
                className={stickyClass ? "sticky" : ""}
                style={{ top: `${headerHeight}px` }}
            >
                <StyledWrapper {...styles.leftWrapper}>
                    <Icon {...styles.warningIcon}></Icon>
                    <Typography {...styles.warningText}>{translate("LoginCalloutMessage")}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.rightWrapper}>
                    <Link href={signInPageUrl} {...styles.link}>
                        {translate("Sign In")}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(LoginCallout),
    definition: {
        displayName: "Login Callout",
        group: "Common",
    },
};

export default widgetModule;
